import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Title1,
  Text,
  Spinner,
  Badge,
  Button,
} from '@fluentui/react-components';
import {
  getGovernanceSummary,
  type GovernanceSummary,
  type GovernanceFilter,
  type GovernanceVerdict,
} from '../api/governance';

// Workbench MVP Session B — Governance dashboard.
// Eight policy gates, chaos toggle across ?filter=all|stress|golden.
// Mirrors ArchitectureExplorer's card + table shell so the two pages read
// as a single Workbench once wired into the sidebar.

const useStyles = makeStyles({
  header: { marginBottom: '18px' },
  toolbar: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '18px',
  },
  toolbarLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    marginRight: '4px',
  },
  kpiGrid: {
    display: 'grid',
    gap: '14px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    marginBottom: '18px',
  },
  kpi: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '14px 16px',
  },
  kpiLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  kpiValue: {
    fontSize: '26px',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums' as const,
    marginTop: '4px',
  },
  gateGrid: {
    display: 'grid',
    gap: '12px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  },
  gateCard: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '14px 16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  gateHeader: {
    display: 'flex',
    justifyContent: 'space-between' as const,
    alignItems: 'center',
  },
  gateName: { fontWeight: 600, fontSize: '15px' },
  gateCategory: { color: tokens.colorNeutralForeground3, fontSize: '12px' },
  gateRow: {
    display: 'flex',
    justifyContent: 'space-between' as const,
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums' as const,
  },
  gateEvidence: {
    color: tokens.colorNeutralForeground2,
    fontSize: '12px',
    marginTop: '4px',
  },
});

function verdictBadge(v: GovernanceVerdict) {
  if (v === 'pass') return <Badge appearance="tint" color="success">pass</Badge>;
  if (v === 'warn') return <Badge appearance="tint" color="warning">warn</Badge>;
  if (v === 'fail') return <Badge appearance="tint" color="danger">fail</Badge>;
  return <Badge appearance="outline" color="informative">idle</Badge>;
}

function fmtObserved(v: number | null, unit: string): string {
  if (v === null || v === undefined) return '—';
  if (unit === 'rate') return `${(v * 100).toFixed(1)}%`;
  if (unit === 'usd') return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 });
  if (unit === 'score') return v.toFixed(2);
  return v.toString();
}

function fmtThreshold(v: number | null, unit: string): string {
  if (v === null || v === undefined) return '—';
  if (unit === 'rate') return `${(v * 100).toFixed(0)}%`;
  if (unit === 'usd') return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  if (unit === 'score') return v.toFixed(2);
  return v.toString();
}

export default function Governance() {
  const s = useStyles();
  const [summary, setSummary] = useState<GovernanceSummary | null>(null);
  const [filter, setFilter] = useState<GovernanceFilter>('all');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    getGovernanceSummary(7, filter)
      .then(setSummary)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div data-testid="governance-page">
      <div className={s.header}>
        <Title1 as="h1">Governance</Title1>
        <Text as="p" size={300}>
          Eight policy-dimension gates over the last {summary?.windowDays ?? 7} days.
          Orthogonal to Architecture Explorer — that view answers "which node is hot",
          this view answers "which policy is holding".
        </Text>
      </div>

      <div className={s.toolbar}>
        <span className={s.toolbarLabel}>Evidence filter</span>
        {(['all', 'golden', 'stress'] as GovernanceFilter[]).map((f) => (
          <Button
            key={f}
            appearance={filter === f ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setFilter(f)}
            data-testid={`governance-filter-${f}`}
          >
            {f}
          </Button>
        ))}
      </div>

      {loading && <Spinner label="Loading governance snapshot…" />}
      {err && (
        <Text as="p" style={{ color: tokens.colorPaletteRedForeground1 }}>
          {err}
        </Text>
      )}

      {summary && !loading && (
        <>
          <div className={s.kpiGrid}>
            <div className={s.kpi}>
              <div className={s.kpiLabel}>Overall verdict</div>
              <div className={s.kpiValue}>{verdictBadge(summary.overallVerdict)}</div>
            </div>
            <div className={s.kpi}>
              <div className={s.kpiLabel}>Traces in-window</div>
              <div className={s.kpiValue}>{summary.evidenceTraceCount}</div>
              <Text size={200}>
                chat {summary.evidenceChatCount} · bench {summary.evidenceBenchmarkCount} · matrix{' '}
                {summary.evidenceMatrixCount}
              </Text>
            </div>
            <div className={s.kpi}>
              <div className={s.kpiLabel}>Filter</div>
              <div className={s.kpiValue}>{summary.filter}</div>
            </div>
          </div>

          <div className={s.gateGrid} data-testid="governance-gates">
            {summary.gates.map((g) => (
              <div key={g.gateId} className={s.gateCard} data-testid={`gate-${g.gateId}`}>
                <div className={s.gateHeader}>
                  <div>
                    <div className={s.gateName}>{g.displayName}</div>
                    <div className={s.gateCategory}>{g.category}</div>
                  </div>
                  {verdictBadge(g.verdict)}
                </div>
                <div className={s.gateRow}>
                  <span>Observed</span>
                  <strong data-testid={`gate-${g.gateId}-observed`}>
                    {fmtObserved(g.observedValue, g.unit)}
                  </strong>
                </div>
                <div className={s.gateRow}>
                  <span>Warn / Fail</span>
                  <span>
                    {fmtThreshold(g.warnThreshold, g.unit)} / {fmtThreshold(g.failThreshold, g.unit)}
                  </span>
                </div>
                <div className={s.gateRow}>
                  <span>Sample size</span>
                  <span>
                    {g.numeratorCount} / {g.denominatorCount}
                  </span>
                </div>
                <div className={s.gateEvidence}>{g.evidence}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
